import Vue from 'vue'
import Cookie from 'js-cookie'
import router from './../../router'

function setAuthToken(state, accessToken) {
  Vue.set(state.authentication, 'backendAccessToken', accessToken)
}

function setAuthCookie(state, accessToken) {
  Cookie.set(state.authentication.cookieName, accessToken, {
    expires: state.authentication.cookieLifeTime,
    secure: true,
  })
}

function availableCubesReceived(state, data) {
  // Set is loading to false to hide the loading screen after receiving the list
  // of available cubes.
  Vue.set(state.global, 'isLoading', false)
  state.availableCubes = Object.entries(data).reduce((list, cube) => {
    list.push({
      cubeName: cube[0],
      nickName: cube[1],
    })
    return list
  }, [])
}

function setIsLoading(state, value = false) {
  Vue.set(state.global, 'isLoading', value)
}

function appInitialized(state, data) {
  const appConfig = data.data.appConfig
  const cube = data.data.cubeDetails
  const customer = data.data.customerInfo
  const sessionSettings = data.data.sessionSettings

  Vue.set(state.global, 'environment', appConfig.appEnv)

  Vue.set(state.cube, 'id', data.data.cubeId)
  Vue.set(
    state.cube,
    'nickName',
    data.data.cubeAuthData.accessObjects[sessionSettings.selectedCubeName]
  )
  Vue.set(state.cube, 'name', sessionSettings.selectedCubeName)

  // The backend can return either '1' or true as positive values.
  const hasProduction =
    customer.hasProduction === true || customer.hasProduction === '1'
  Vue.set(state.cube, 'hasProduction', hasProduction)
  Vue.set(state.cube, 'productionType', customer.productionType.toLowerCase())

  Vue.set(state.cube.supplier, 'logoUrl', appConfig.supplierLogo)
  Vue.set(state.cube.supplier, 'iconUrl', appConfig.supplierIcon)

  Vue.set(state.cube, 'localHostName', cube.localIp)
  Vue.set(state.cube, 'localChartServicePort', cube.localChartServicePort)
  Vue.set(state.cube, 'localWebSocketPort', cube.localWebSocketPort)

  Vue.set(state.cube, 'remoteHostName', cube.remoteHostName)
  // Note that cube.remoteHostName does not include a url schema, as of February 2021.
  Vue.set(
    state.urls,
    'deviceControl',
    `https://${cube.remoteHostName}/api/app/v1.0/device-control/`
  )
  Vue.set(
    state.urls,
    'deviceScheduler',
    `https://${cube.remoteHostName}/api/app/v1.0/device-scheduler/`
  )
  Vue.set(
    state.urls,
    'deviceNetworkManager',
    `https://${cube.remoteHostName}/api/app/v1.0/device-network-manager/`
  )
  Vue.set(state.cube, 'remoteChartServicePort', cube.remoteChartServicePort)
  Vue.set(state.cube, 'remoteWebSocketPort', cube.remoteWebSocketPort)
  Vue.set(state.cube, 'remoteWebSocketPath', cube.remoteWebSocketPath || '/app')

  Vue.set(state.cube, 'latitude', parseFloat(customer.latitude))
  Vue.set(state.cube, 'longitude', parseFloat(customer.longitude))
  Vue.set(
    state.cube,
    'unusedEnergyLines',
    parseInt(customer.energyLinesAvailable)
  )
  Vue.set(
    state.cube,
    'openConnectionsAvailable',
    parseInt(customer.openConnectionsAvailable)
  )
  Vue.set(state.cube, 'showInverterData', customer.useInverterData === '1')

  Vue.set(state.cube, 'planName', customer.tariff)
  Vue.set(state.cube, 'iotDeviceId', customer.iotDeviceId)

  Vue.set(state.cube, 'notifications', data.data.subscription.inbox)

  Vue.set(state.cube, 'descriptionText', customer.infoWindowText)

  // Transform the crop metadata. See comment in Personalize.vue.
  if (customer.infoWindowImageDetails.cropData) {
    const crop = customer.infoWindowImageDetails.cropData

    let orientation = 1
    switch (crop.orientation) {
      case 90:
        orientation = 8
        break

      case 180:
        orientation = 3
        break

      case 270:
        orientation = 6
        break
    }

    customer.infoWindowImageDetails.cropData = {
      scale: parseFloat(crop.scale) * state.cube.imageCroppaQuality,
      startX: parseFloat(crop.x) * state.cube.imageCroppaQuality * -1,
      startY: parseFloat(crop.y) * state.cube.imageCroppaQuality * -1,
      orientation: parseInt(orientation),
    }
  }
  Vue.set(state.cube, 'images', customer.infoWindowImageDetails)

  Vue.set(
    state.settings.producerSettings,
    'minDefaultSellingPrice',
    parseFloat(appConfig.tariffSettings.minPriceNet)
  )
  Vue.set(
    state.settings.producerSettings,
    'maxDefaultSellingPrice',
    parseFloat(appConfig.tariffSettings.maxPriceNet)
  )
  Vue.set(
    state.settings.producerSettings,
    'defaultSellingPriceStepSize',
    parseFloat(appConfig.tariffSettings.stepSize)
  )

  // Google maps setup.
  Vue.set(state.global, 'googleMapsApiKey', appConfig.googleMapsApiKey)
  Vue.set(state.global, 'googleMapsApiUrl', appConfig.googleMapsApiUrl)
  Vue.set(
    state.global,
    'googleMapsJavascriptBaseUrl',
    appConfig.googleMapsJavascriptBaseUrl
  )
  Vue.set(
    state.global,
    'googleMapsStaticApiBaseUrl',
    appConfig.googleMapsStaticApiBaseUrl
  )

  // We default the map center to the current cube.
  Vue.set(state.map, 'center', {
    latitude: customer.latitude,
    longitude: customer.longitude,
  })

  // Push api, notifications.
  // Possible values for notificationsValue and webPushValue:
  // -1: The user actively disabled notifications.
  // 0: The user did not decide yet (default).
  // 1: The user enabled notifications (possibly in another app instance).
  // 2: (webPushValue only) The user enabled notifications, but the backend does
  //    not have a web push subscription for the current session.
  Vue.set(
    state.cube,
    'notificationsValue',
    parseInt(data.data.subscription.notificationEnabled)
  )
  Vue.set(
    state.cube,
    'webPushValue',
    parseInt(data.data.subscription.subscriptionEnabled)
  )

  Vue.set(state.authentication, 'userIsAuthenticated', true)
  if (data.data.userId) {
    // We're using userId (login name or email) as user name and need to store
    // it because we also store the currently selected cube per user in cookies.
    // In order to allow users to change the currently selected contract/cube we
    // need to have the username available to update the correct cookie.
    Vue.set(state.authentication, 'userName', data.data.userId)
  }

  // Set appInitialized last so that when this is set, we know everything's ready.
  Vue.set(state.global, 'appInitialized', true)
}

function userAuthenticationRequestFinished(state, result) {
  if (!result || !result.data || !result.status) {
    // Something with the request went wrong.
    return false
  }

  const data = result.data
  if (data.error_description) {
    Vue.set(state.authentication, 'errorMessage', data.error_description)
  } else if (data.access_token) {
    setAuthCookie(state, data.access_token)

    // Clearing out any shown errors.
    Vue.set(state.authentication, 'errorMessage', '')

    // Note that Vue.set needs to be executed *after* setting the cookie, since
    // any dom updates will be checked at that time and in this case depends on
    // the cookie being set.
    Vue.set(state.authentication, 'userIsAuthenticated', true)
  }
}

function userAuthenticationFailed(state, message) {
  Vue.set(state.authentication, 'errorMessage', message)
}

function userLoggedOut(state) {
  Cookie.remove(state.authentication.cookieName)

  // Note that Vue.set needs to be executed *after* setting the cookie, since
  // any dom updates will be checked at that time and in this case depends on
  // the cookie being set.
  Vue.set(state.authentication, 'userIsAuthenticated', false)
  Vue.set(state.authentication, 'userName', false)
  Vue.set(state.authentication, 'errorMessage', '')
  Vue.set(state.authentication, 'backendAccessToken', false)

  // We need to set isLoading to false because we're using userLoggedOut when a
  // user has an invalid auth token when starting the app.
  // This way the app knows that loading is done already and can show the login
  // form.
  Vue.set(state.global, 'isLoading', false)

  // Since appInitialized is true when everything (inluding authentication) is set
  // up and the user can start using the app - we need to set this to false when
  // logging out.
  Vue.set(state.global, 'appInitialized', false)

  Vue.set(state.global, 'isInLocaNetwork', false)

  Vue.set(state, 'availableCubes', [])

  Vue.set(state.cube, 'id', false)
  Vue.set(state.cube, 'name', false)
  Vue.set(state.cube, 'nickName', false)
  Vue.set(state.cube, 'latitude', false)
  Vue.set(state.cube, 'longitude', false)
  Vue.set(state.cube, 'unusedEnergyLines', 0)
  Vue.set(state.cube, 'producers', [])
  Vue.set(state.cube, 'consumers', [])
  Vue.set(state.cube, 'consumerPricesLoaded', false)

  Vue.set(state.map.center, 'latitude', null)
  Vue.set(state.map.center, 'longitude', null)
  Vue.set(state.map, 'selectedCube', null)
  Vue.set(state.map, 'globalCubesList', [])
  Vue.set(state.map, 'globalProducerPrices', {})

  Vue.set(state.currentReads, 'dotIndex', 0)
  Vue.set(state.currentReads, 'balance', 0)
  Vue.set(state.currentReads, 'consumption', 0)
  Vue.set(state.currentReads, 'production', 0)
  Vue.set(state.currentReads, 'fromCommunity', 0)
  Vue.set(state.currentReads, 'fromSupplier', 0)
  Vue.set(state.currentReads.dotCoordinates, 'positiveX', 0)
  Vue.set(state.currentReads.dotCoordinates, 'negativeX', 0)
  Vue.set(state.currentReads.dotCoordinates, 'positiveY', 0)
  Vue.set(state.currentReads.dotCoordinates, 'negativeY', 0)

  Vue.set(state.settings.contractData, 'loaded', false)
  Vue.set(state.settings.contractData, 'meterPointId', '')
  Vue.set(state.settings.contractData, 'contractId', '')
  Vue.set(state.settings.contractData, 'firstName', '')
  Vue.set(state.settings.contractData, 'lastName', '')
  Vue.set(state.settings.contractData, 'nickName', '')
  Vue.set(state.settings.contractData, 'phone', '')
  Vue.set(state.settings.contractData, 'email', '')

  Vue.set(state.settings.contractData.cubeAddress, 'address', '')
  Vue.set(state.settings.contractData.cubeAddress, 'zip', '')
  Vue.set(state.settings.contractData.cubeAddress, 'city', '')
  Vue.set(state.settings.contractData.cubeAddress, 'country', '')
  Vue.set(state.settings.contractData.billingAddress, 'address', '')
  Vue.set(state.settings.contractData.billingAddress, 'zip', '')
  Vue.set(state.settings.contractData.billingAddress, 'city', '')
  Vue.set(state.settings.contractData.billingAddress, 'country', '')

  Vue.set(state.settings, 'selectedInvoiceYear', false)
  Vue.set(state.settings, 'invoices', [])

  Vue.set(state.historyData, 'received', false)
  Vue.set(state.historyData, 'labels', [])
  Vue.set(state.historyData.datasets, 'fromSupplier', [])
  Vue.set(state.historyData.datasets, 'fromCommunity', [])
  Vue.set(state.historyData.datasets, 'toCommunity', [])
  Vue.set(state.historyData.datasets, 'communityConsumable', [])
  Vue.set(state.historyData.datasets, 'inverterProduction', [])

  router.push({
    name: 'login',
  })
}

function setGoogleMapsStatus(state, status) {
  state.global.googleMapsJavascriptStatus = status
  if (status === 'loaded') {
    state.global.googleMapsJavascriptLoaded = true
  }
}

function mapCenterChanged(state, newCenter) {
  Vue.set(state.map, 'center', newCenter)
}

function setFormState(state, data) {
  if (!data.form) {
    throw new Error(`Error setting form state: data.form missing.`)
  }
  if (!state.forms[data.form]) {
    Vue.set(state.forms, data.form, {
      state: data.state,
      message: false,
    })
  } else {
    Vue.set(state.forms[data.form], 'state', data.state)
  }
}

function serviceWorkerRegistered(state, serviceWorkerRegistration) {
  if (serviceWorkerRegistration) {
    Vue.set(
      state.global,
      'serviceWorkerRegistration',
      serviceWorkerRegistration
    )
  }
}

function setPushSubscription(state, subscription) {
  Vue.set(state.global, 'pushSubscription', subscription)
  Vue.set(state.cube, 'webPushValue', subscription ? 1 : -1)
}

function enableNotifications(state, enable) {
  enable = !!enable
  Vue.set(state.cube, 'notificationsValue', enable ? 1 : -1)
  Vue.set(state.cube, 'webPushvalue', enable ? 1 : -1)

  if (enable && !state.cube.notificationSound) {
    const notificationSound = new Audio('/notification.mp3')
    Vue.set(state.cube, 'notificationSound', notificationSound)
  }
}

function showSlidein(state, data) {
  if (typeof data.show === 'undefined') {
    data.show = 'toggle'
  }

  if (data.show === 'toggle') {
    data.show = !state.global.slideins[data.slidein].visible
  }

  Vue.set(state.global.slideins[data.slidein], 'visible', data.show)
}

function hideAllSlideins(state) {
  Object.entries(state.global.slideins).map((row) => {
    Vue.set(state.global.slideins[row[0]], 'visible', false)
  })
}

function showNotificationsRequestPopup(state, newValue) {
  Vue.set(state.cube, 'showNotificationsRequestPopup', !!newValue)
}

function markNotificationsAsRead(state, data) {
  if (data.notification) {
    const index = state.cube.notifications.findIndex((notification) => {
      return notification.id === data.notification.id
    })

    if (index > -1) {
      Vue.set(state.cube.notifications[index], 'unread', data.isRead)
    }
  } else if (data.fromCubeName) {
    state.cube.notifications.map((notification, index) => {
      if (notification.triggeringcubename === data.fromCubeName) {
        Vue.set(state.cube.notifications[index], 'unread', data.isRead)
      }
    })
  } else if (data.all) {
    state.cube.notifications.map((notification, index) => {
      Vue.set(state.cube.notifications[index], 'unread', data.isRead)
    })
  }
}

function setNotificationSound(state, sound) {
  Vue.set(state.cube, 'notificationSound', sound)
}

function addNotification(state, notification) {
  state.cube.notifications.unshift(notification)
}

function toggleMapSideBar(state) {
  state.global.mapSideBar.visible = !state.global.mapSideBar.visible
}

function showMapSideBar(state) {
  state.global.mapSideBar.visible = true
}

function hideMapSideBar(state) {
  state.global.mapSideBar.visible = false
}

function setProductionTypes(state, productionTypes) {
  state.strings.productionTypes = productionTypes
}

function setCubeOrder(state, orderType) {
  state.settings.cubeOrder = orderType
}

function setSupplierData(state, data) {
  state.cube.supplier = { ...state.cube.supplier, ...data }
}

export default {
  setIsLoading,
  appInitialized,
  availableCubesReceived,
  mapCenterChanged,
  serviceWorkerRegistered,
  setAuthCookie,
  setAuthToken,
  setFormState,
  setGoogleMapsStatus,
  setPushSubscription,
  userAuthenticationFailed,
  userAuthenticationRequestFinished,
  userLoggedOut,
  enableNotifications,
  showSlidein,
  hideAllSlideins,
  showNotificationsRequestPopup,
  markNotificationsAsRead,
  setNotificationSound,
  addNotification,
  toggleMapSideBar,
  showMapSideBar,
  hideMapSideBar,
  setProductionTypes,
  setCubeOrder,
  setSupplierData,
}
